import React, { useRef, useState, useEffect } from 'react';
import { Upload, Modal, Button, Progress, message } from 'antd';
import { DeleteOutlined, EyeOutlined } from '@ant-design/icons';
import type { UploadProps, UploadFile } from 'antd';
import { imageEmpty } from 'utils/importFile';
import { ButtonCommon } from '../Button/ButtonCommon';
import { CloudArrowUp, Upload as UploadIcon } from '@phosphor-icons/react';
import showAlertNotification from '../Modal/AlertNotification';
import { useUploadToS3Mutation, useLazyGetPresignedUrlQuery } from 'services';
import { AxiosProgressEvent } from 'axios';
import { getFilePath } from 'utils';
import { UploadImageCommonProps } from 'types/common';

const getBase64 = (file: File | Blob): Promise<string> =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = () => resolve(reader.result as string);
    reader.onerror = (error) => reject(error);
  });

const UploadImageCommon: React.FC<UploadImageCommonProps> = ({ imagesData, setImagesData, isDisabled = false }) => {
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewImage, setPreviewImage] = useState('');
  const [fileList, setFileList] = useState<UploadFile[]>([]);
  const [uploadToS3] = useUploadToS3Mutation();
  const [getPresignedUrl] = useLazyGetPresignedUrlQuery();
  const uploadingCountRef = useRef(0);
  const maxCount = 5;
  const shownErrorRef = useRef(false);
  const initializedRef = useRef(false);

  // Initialize fileList from imagesData when it changes and contains items
  useEffect(() => {
    if (imagesData?.length > 0 && !initializedRef.current) {
      const newFileList: UploadFile[] = imagesData.map((image, index) => ({
        uid: `existing-${index}`,
        name: image.name || `image-${index}`,
        status: 'done',
        url: `${getFilePath()}/${image.filePath}`,
        thumbUrl: `${getFilePath()}/${image.filePath}`,
      }));

      setFileList(newFileList);
      initializedRef.current = true;
    }
  }, [imagesData]);

  const handlePreview = async (file: UploadFile) => {
    if (!file.url && !file.preview) {
      file.preview = await getBase64(file.originFileObj as File);
    }
    setPreviewImage(file.url || (file.preview as string));
    setPreviewOpen(true);
  };

  // Handle preview for existing images
  const handleExistingImagePreview = (imagePath: string) => {
    setPreviewImage(`${getFilePath()}/${imagePath}`);
    setPreviewOpen(true);
  };

  const handleChange: UploadProps['onChange'] = ({ fileList: newFileList }) => {
    setFileList(newFileList.slice(0, maxCount));
  };

  const handleCancel = () => setPreviewOpen(false);

  const handleRemove = (file: UploadFile) => {
    // Handle removal of existing images from imagesData
    if (file.uid.startsWith('existing-')) {
      const index = parseInt(file.uid.split('-')[1]);

      setImagesData((prev) => {
        const updated = prev
          .filter((_, idx) => idx !== index)
          .map((item, idx) => ({
            ...item,
            order: idx + 1,
          }));
        return updated;
      });
    } else {
      // Handle removal of newly uploaded files
      const removedIndex = fileList.findIndex((item) => item.uid === file.uid);
      setImagesData((prev) => {
        const updated = prev
          .filter((_, index) => index !== removedIndex)
          .map((item, idx) => ({
            ...item,
            order: idx + 1,
          }));
        return updated;
      });
    }

    setFileList((prevList) => prevList.filter((item) => item.uid !== file.uid));
    message.success('Hình ảnh đã được xóa');
  };

  // Handle removal of existing images by index
  const handleRemoveExistingImage = (index: number) => {
    setImagesData((prev) => {
      const updated = prev
        .filter((_, idx) => idx !== index)
        .map((item, idx) => ({
          ...item,
          order: idx + 1,
        }));
      return updated;
    });

    setFileList((prevList) => prevList.filter((item) => item.uid !== `existing-${index}`));

    message.success('Hình ảnh đã được xóa');
  };

  const uploadButton = (
    <div className='flex justify-center items-center gap-6'>
      <CloudArrowUp size={48} weight='thin' />
      <div className='flex justify-between items-center w-full'>
        <div className='text-left'>
          <p>Chọn hoặc kéo thả hình ảnh sản phẩm vào đây...</p>
          <p className='text-[var(--textBlack56)]'>Hình ảnh ở định dạng img, png, jpeg,... và không vượt quá 2Mb</p>
        </div>
        <ButtonCommon
          color='basic'
          fullWidth
          radius='rounded'
          size='sm'
          className='bg-[var(--bgBlackOpacity)] max-w-[132px] text-black gap-2'
          icon={<UploadIcon size={20} />}
          iconPosition='left'
        >
          <span className='whitespace-nowrap'>Chọn Tệp Tin</span>
        </ButtonCommon>
      </div>
    </div>
  );

  return (
    <>
      {!isDisabled && (
        <Upload.Dragger
          listType='picture-card'
          fileList={fileList}
          className={`p-0 upload-image-drag ${fileList.length >= maxCount || imagesData?.length >= maxCount ? 'opacity-50 pointer-events-none' : ''}`}
          onPreview={handlePreview}
          onChange={handleChange}
          onRemove={handleRemove}
          beforeUpload={async (file) => {
            const totalCount = uploadingCountRef.current + (imagesData?.length || 0);

            if (totalCount >= maxCount) {
              if (!shownErrorRef.current) {
                showAlertNotification({
                  type: 'error',
                  title: 'Tải hình ảnh thất bại',
                  message: `Bạn chỉ được tải tối đa ${maxCount} hình.`,
                });
                shownErrorRef.current = true;
              }
              return Upload.LIST_IGNORE;
            }

            uploadingCountRef.current += 1;
            const isImage = ['image/jpeg', 'image/png', 'image/jpg', 'image/gif', 'image/bmp', 'image/webp'].includes(
              file.type,
            );
            const isLt2M = file.size / 1024 / 1024 <= 2;

            if (!isImage || !isLt2M) {
              if (!shownErrorRef.current) {
                showAlertNotification({
                  type: 'error',
                  title: 'Tải hình ảnh thất bại',
                  message: !isImage
                    ? 'Định dạng không hợp lệ. Chỉ chấp nhận các định dạng: jpg, jpeg, png, gif, bmp, webp.'
                    : 'Kích thước hình ảnh phải nhỏ hơn hoặc bằng 2MB.',
                });
                shownErrorRef.current = true;
              }
              shownErrorRef.current = false;
              uploadingCountRef.current -= 1;
              return Upload.LIST_IGNORE;
            }
            shownErrorRef.current = false;
            try {
              const fileName = `${Date.now()}-${file.name}`;
              const fileType = file.type;

              const { preSignedURL, keyFile } = await getPresignedUrl({ fileName, fileType }).unwrap();
              const newFileItem: UploadFile = {
                uid: file.uid,
                name: file.name,
                status: 'uploading',
                percent: 0,
                originFileObj: file,
              };
              setFileList((prev) => [...prev, newFileItem]);
              await uploadToS3({
                preSignedURL,
                file,
                onUploadProgress: (event: AxiosProgressEvent) => {
                  if (event.total) {
                    const percent = Math.round((event.loaded * 100) / event.total);
                    setFileList((prevList) =>
                      prevList.map((item) =>
                        item.uid === file.uid
                          ? { ...item, percent, status: percent === 100 ? 'done' : 'uploading' }
                          : item,
                      ),
                    );
                  }
                },
              }).unwrap();
              const uploadedImageUrl = keyFile.split('?')[0];

              setImagesData((prev) => [
                ...prev,
                {
                  filePath: uploadedImageUrl,
                  name: fileName,
                  order: prev.length + 1,
                },
              ]);
            } catch (error) {
              console.error('Upload failed:', error);
              message.error('Tải hình ảnh thất bại');
              setFileList((prevList) =>
                prevList.map((item) => (item.uid === file.uid ? { ...item, status: 'error' } : item)),
              );
            } finally {
              uploadingCountRef.current -= 1;
            }

            return Upload.LIST_IGNORE;
          }}
          multiple
        >
          {uploadButton}
        </Upload.Dragger>
      )}

      <Modal open={previewOpen} footer={null} onCancel={handleCancel} centered>
        <img alt='preview' style={{ width: '100%' }} src={previewImage} />
      </Modal>

      <div className='flex gap-4 mt-4 flex-wrap'>
        {Array.from({ length: 5 }).map((_, index) => (
          <div key={index} className='w-[84px] h-[84px] relative'>
            {imagesData && imagesData[index]?.filePath ? (
              <>
                <img
                  src={`${getFilePath()}/${imagesData[index].filePath}`}
                  alt='thumbnail'
                  className='w-full h-full object-cover rounded-lg'
                />
                {!isDisabled && (
                  <>
                    <Button
                      icon={<EyeOutlined />}
                      size='small'
                      className='absolute top-2 left-2 bg-black bg-opacity-50 border-none text-white rounded-full'
                      onClick={() => handleExistingImagePreview(imagesData[index].filePath)}
                    />
                    <Button
                      icon={<DeleteOutlined />}
                      size='small'
                      className='absolute top-2 right-2 bg-red-600 bg-opacity-70 border-none text-white rounded-full'
                      onClick={() => handleRemoveExistingImage(index)}
                    />
                  </>
                )}
              </>
            ) : (
              <>
                {fileList[index] && fileList[index].status === 'done' ? (
                  <img
                    src={
                      fileList[index].thumbUrl ||
                      (fileList[index].preview as string) ||
                      (fileList[index].originFileObj ? URL.createObjectURL(fileList[index].originFileObj as Blob) : '')
                    }
                    alt='thumbnail'
                    className='w-full h-full object-cover rounded-lg'
                  />
                ) : fileList[index]?.status === 'uploading' ? (
                  <Progress percent={fileList[index]?.percent} status='active' className='w-[80%]' />
                ) : (
                  <img src={imageEmpty} alt='No products available' />
                )}
              </>
            )}

            {fileList[index] && fileList[index].status === 'done' && !imagesData?.[index]?.filePath && (
              <>
                <Button
                  icon={<EyeOutlined />}
                  size='small'
                  className='absolute top-2 left-2 bg-black bg-opacity-50 border-none text-white rounded-full'
                  onClick={() => handlePreview(fileList[index])}
                />
                <Button
                  icon={<DeleteOutlined />}
                  size='small'
                  className='absolute top-2 right-2 bg-red-600 bg-opacity-70 border-none text-white rounded-full'
                  onClick={() => handleRemove(fileList[index])}
                />
              </>
            )}
          </div>
        ))}
      </div>
    </>
  );
};

export default UploadImageCommon;
